import { useCallback, useState } from "react";
import "./StrategyManagement.styles.scss";

// Components Imports
import { StrategyTable } from "./components";

// Hooks Imports
import { useCreateStrategyMutation } from "./services/strategyManagementApi";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";

// Material UI Imports
import { Button, SvgIcon, TextField, useTheme } from "@mui/material";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

const StrategyManagement = () => {
    const theme = useTheme();
    const { showError } = useAlerts();
    const [displayName, setDisplayName] = useState("");
    const [createStrategy, { isLoading: isCreating }] = useCreateStrategyMutation();

    const handleCreate = useCallback(async () => {
        const trimmed = displayName.trim();
        if (!trimmed) return;
        try {
            await createStrategy({ data: { display_name: trimmed } }).unwrap();
            setDisplayName("");
        } catch (err) {
            showError(`Failed to create strategy: ${getErrorMessage(err)}`);
        }
    }, [displayName, createStrategy, showError]);

    return (
        <div className="pulse-sidenav-adjusted-container" style={{ backgroundColor: theme.palette.bg["900"] }}>
            <SvgIcon
                sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                }}
                children={<LogoSVG style={{ color: theme.palette.bg[800] }} />}
            />

            <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-row pulse-mb-1">
                Strategy Management
            </p>

            <div className="pulse-row pulse-mb-1" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <TextField
                    size="small"
                    label="Display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    sx={{
                        width: 280,
                        "& .MuiOutlinedInput-root": {
                            color: theme.palette.bg["50"],
                            backgroundColor: theme.palette.bg["800"],
                            "& fieldset": { borderColor: theme.palette.bg["400"] },
                            "&:hover fieldset": { borderColor: theme.palette.bg["300"] },
                            "&.Mui-focused": {
                                backgroundColor: theme.palette.bg["900"],
                                "& fieldset": { borderColor: theme.palette.bg["50"] },
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                                color: theme.palette.bg["50"],
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: theme.palette.bg["200"],
                            "&.Mui-focused": { color: theme.palette.bg["50"] },
                        },
                    }}
                />
                <Button
                    variant="contained"
                    disabled={!displayName.trim() || isCreating}
                    onClick={handleCreate}
                    sx={{
                        color: theme.palette.bg["50"],
                        backgroundColor: (theme.palette.success as unknown as Record<string, string>)["800"],
                        "&:hover": {
                            backgroundColor: (theme.palette.success as unknown as Record<string, string>)["700"],
                        },
                        "&.Mui-focusVisible": {
                            backgroundColor: theme.palette.bg["50"],
                            color: theme.palette.bg["900"],
                        },
                        "&:disabled": {
                            backgroundColor: theme.palette.bg["main"],
                            color: theme.palette.bg["900"],
                        },
                    }}
                >
                    {isCreating ? "Creating…" : "Create Strategy"}
                </Button>
            </div>

            <StrategyTable />
        </div>
    );
};

export default StrategyManagement;